import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guest mode: browse the app (read-only) without an account. Any action that
  // needs an account (create key / playground) prompts registration.
  const [guest, setGuest] = useState(() => localStorage.getItem('guest') === '1');

  const enterGuest = useCallback(() => {
    localStorage.setItem('guest', '1');
    setGuest(true);
  }, []);
  const exitGuest = useCallback(() => {
    localStorage.removeItem('guest');
    setGuest(false);
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.me();
      setUser(res.data.user);
    } catch {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: GoogleCallback exchanges the session_id and
    // sets the token itself — skip the /me check to avoid a race.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    loadUser();
  }, [loadUser]);

  const login = async (mobile, password) => {
    const res = await api.login({ mobile, password });
    localStorage.setItem('token', res.data.token);
    localStorage.removeItem('guest');
    setGuest(false);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.register(data);
    localStorage.setItem('token', res.data.token);
    localStorage.removeItem('guest');
    setGuest(false);
    setUser(res.data.user);
    return res.data; // { user, gift_granted, gift_amount_egp }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('guest');
    setGuest(false);
    setUser(null);
    window.location.href = '/';
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.me();
      setUser(res.data.user);
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, guest, enterGuest, exitGuest, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
